
import React from "react";
import { Control } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { Trash2, Edit } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface SubscriptionItemProps {
  control: Control<any>;
  field: any;
  index: number;
  workoutTypeName: string;
  workoutTypeColor: string;
  isOpen: boolean;
  onToggle: () => void;
  onRemove: () => void;
}

const SubscriptionItem: React.FC<SubscriptionItemProps> = ({
  control,
  field,
  index,
  workoutTypeName,
  workoutTypeColor,
  isOpen,
  onToggle,
  onRemove
}) => {
  const { t } = useTranslation();

  return (
    <Card className="overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: workoutTypeColor }}
                />
                <span className="greek-text">{workoutTypeName}</span>
                <Edit className="h-4 w-4 text-muted-foreground" />
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={control}
                name={`subscriptions.${index}.sessionsPerMonth`}
                rules={{
                  required: t('clients.allFieldsRequired'),
                  validate: (value) => {
                    const num = Number(value);
                    if (isNaN(num) || num < 1 || num > 30) {
                      return t('clients.sessionsPerMonthValidation');
                    }
                    return true;
                  }
                }}
                render={({ field: formField, fieldState }) => (
                  <FormItem>
                    <FormLabel className="text-sm text-muted-foreground">
                      {t('clients.monthlyLimit')}
                    </FormLabel>
                    <FormControl>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          max="30"
                          placeholder={t('clients.enterSessionsPlaceholder')}
                          className="h-10 text-center text-sm"
                          value={formField.value || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            formField.onChange(value);
                          }}
                          onBlur={formField.onBlur}
                        />
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {t('clients.sessionsPerMonth')}
                        </span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name={`subscriptions.${index}.totalPrice`}
                rules={{
                  required: t('clients.allFieldsRequired'),
                  validate: (value) => {
                    const num = Number(value);
                    if (isNaN(num) || num <= 0) {
                      return t('clients.totalPriceValidation');
                    }
                    return true;
                  }
                }}
                render={({ field: formField, fieldState }) => (
                  <FormItem>
                    <FormLabel className="text-sm text-muted-foreground">
                      {t('clients.totalSubscriptionPrice')} (€)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        placeholder={t('clients.enterTotalPricePlaceholder')}
                        className="h-10 text-sm"
                        value={formField.value || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          formField.onChange(value);
                        }}
                        onBlur={formField.onBlur}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            {/* Hidden field for workout type ID */}
            <FormField
              control={control}
              name={`subscriptions.${index}.workoutTypeId`}
              render={({ field: formField }) => (
                <input type="hidden" {...formField} value={formField.value} />
              )}
            />
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default SubscriptionItem;
